import { ROUTINE_PLANS } from "../data/beginnerRoutines";
import { getExerciseLog, saveExerciseLog } from "../utils/localStorageUtils";

export const DEFAULT_PLANNED_SETS_COUNT = 3;
export const DEFAULT_PLANNED_REPS = "10";

/**
 * Generates an array of default planned sets for an exercise.
 * @param {number} [count=DEFAULT_PLANNED_SETS_COUNT] Number of sets to generate.
 * @param {string} [reps=DEFAULT_PLANNED_REPS] Target repetitions.
 * @returns {Array<{weight: string, reps: string, type: string}>}
 */
export function generateRoutineSets(count = DEFAULT_PLANNED_SETS_COUNT, reps = DEFAULT_PLANNED_REPS) {
    return Array.from({ length: count }, () => ({
        weight: "",
        reps: String(reps),
        type: "planned",
    }));
}

/**
 * Safely applies a beginner routine plan to a specific date's exercise log.
 * Preserves any exercises that were already logged on that date.
 * 
 * @param {string} date Target date in YYYY-MM-DD format.
 * @param {string} focusId Routine focus identifier (e.g. "top", "bottom", "push", "pull", "full_body_a", "full_body_b").
 * @param {Object} [options]
 * @param {number} [options.setsCount] Custom set count per exercise.
 * @param {string} [options.reps] Custom target reps.
 * @returns {{ success: boolean, addedCount: number, existingCount: number, exercises: string[] }}
 */
export function applyRoutineToDateLog(date, focusId, options = {}) {
    if (!date || typeof date !== "string") {
        throw new Error("A valid date string (YYYY-MM-DD) is required.");
    }

    const plan = ROUTINE_PLANS[focusId];
    if (!plan || !Array.isArray(plan) || plan.length === 0) {
        throw new Error(`Unknown or empty routine plan for focus: ${focusId}`);
    }

    const setsCount = options.setsCount || DEFAULT_PLANNED_SETS_COUNT;
    const reps = options.reps || DEFAULT_PLANNED_REPS;

    const fullLog = getExerciseLog() || {};
    const dateLog = { ...(fullLog[date] || {}) };

    let addedCount = 0;
    let existingCount = 0;
    const plannedExerciseIds = [];

    for (const slot of plan) {
        const exerciseId = slot.defaultExerciseId;
        plannedExerciseIds.push(exerciseId);

        if (dateLog[exerciseId] && Array.isArray(dateLog[exerciseId]) && dateLog[exerciseId].length > 0) {
            // Safe append: Preserve existing logged sets without overwriting
            existingCount += 1;
        } else {
            dateLog[exerciseId] = generateRoutineSets(setsCount, reps);
            addedCount += 1;
        }
    }

    fullLog[date] = dateLog;
    const saved = saveExerciseLog(fullLog);

    return {
        success: Boolean(saved),
        addedCount,
        existingCount,
        exercises: plannedExerciseIds,
    };
}
